// src/services/session.js
// W-4: the ONE web session-renewal implementation. Both axios instances the
// pages use — services/api.js (the default `api`) and the AuthContext
// instance (useAuth().api) — install it with installSessionRenewal().
//
// When a request answers 401 because the access token expired:
//   1. renew once: POST /accounts/refresh/ {refresh}; store the new access
//      token AND the rotated refresh token the response carries (the backend
//      runs ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION, so the old
//      refresh token is dead after one use);
//   2. retry the original request once with the new token;
//   3. single-flight: concurrent 401s share one renewal and all retry;
//   4. two tabs share localStorage: if the renewal fails but the stored
//      refresh token changed while it ran (another tab renewed first), try
//      once more with that token before giving up;
//   5. if renewal fails: remove the session keys and go to /login.
// 401s from the public auth endpoints (login, refresh, register, password
// reset, verify email, invitation accept, logout) are never intercepted, so a
// wrong password still shows its own error on the login page.
// v1.4.1 C7: every refresh carries `idle_seconds` — the time since the last
// real user input (services/idleSession.js, never polling) — so the server
// refuses a session idle past the role's limit even while a tab polls.
import axios from 'axios';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const SESSION_KEYS = { access: 'access_token', refresh: 'refresh_token', user: 'lpms_user' };

const AUTH_ENDPOINTS = [
  /\/accounts\/login\/?$/,
  /\/accounts\/refresh\/?$/,
  /\/accounts\/register\/?$/,
  /\/accounts\/password-reset(\/confirm)?\/?$/,
  /\/accounts\/verify-email\/?$/,
  /\/accounts\/invitations\/accept\/?$/,
  /\/accounts\/logout\/?$/,
];

export function isAuthEndpoint(url) {
  const path = String(url || '').split(/[?#]/)[0];
  return AUTH_ENDPOINTS.some((re) => re.test(path));
}

// Indirection so tests can observe the redirect (jsdom has no navigation).
export const sessionHooks = {
  redirectToLogin() {
    // The login page does not read a return path (it always goes to the
    // role's home), so this is plain /login. Skip it when already there.
    if (window.location.pathname !== '/login') window.location.assign('/login');
  },
  // C7: seconds since the last user input, or null (installed by idleSession.js).
  idleSeconds: () => null,
};

export function endSession() {
  Object.values(SESSION_KEYS).forEach((k) => localStorage.removeItem(k));
  sessionHooks.redirectToLogin();
}

async function postRefresh(refresh) {
  // Plain axios: the refresh call must not pass through these interceptors.
  const idle = sessionHooks.idleSeconds?.();
  const body = Number.isFinite(idle) && idle >= 0 ? { refresh, idle_seconds: Math.floor(idle) } : { refresh };
  const { data } = await axios.post(`${API_BASE}/accounts/refresh/`, body);
  const access = data?.access;
  if (!access) throw new Error('refresh response carried no access token');
  localStorage.setItem(SESSION_KEYS.access, access);
  if (data.refresh) localStorage.setItem(SESSION_KEYS.refresh, data.refresh);
  return access;
}

async function renew() {
  const started = localStorage.getItem(SESSION_KEYS.refresh);
  if (!started) throw new Error('no refresh token');
  try {
    return await postRefresh(started);
  } catch (err) {
    const now = localStorage.getItem(SESSION_KEYS.refresh);
    if (now && now !== started) return postRefresh(now);   // another tab renewed first
    throw err;
  }
}

let inFlight = null;

// Single-flight: every caller during one renewal gets the same promise.
export function renewSession() {
  if (!inFlight) inFlight = renew().finally(() => { inFlight = null; });
  return inFlight;
}

export function installSessionRenewal(instance) {
  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err?.config;
      if (err?.response?.status !== 401 || !original || original._sessionRetried
          || isAuthEndpoint(original.url)) {
        return Promise.reject(err);
      }
      original._sessionRetried = true;
      let access;
      try {
        access = await renewSession();
      } catch {
        endSession();
        return Promise.reject(err);
      }
      if (original.headers) {
        if (typeof original.headers.set === 'function') original.headers.set('Authorization', `Bearer ${access}`);
        else original.headers.Authorization = `Bearer ${access}`;
      }
      return instance(original);
    },
  );
  return instance;
}
